import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

const timelineItems = [
  {
    year: '1901',
    title: '英国における家事使用人の規模',
    body: '英国の人口は3,800万人、家事使用人は女性1,700,000人・男性140,000人だった。',
    source: 'The International Butler Academy 公式サイト',
    sourceUrl: 'https://www.butlerschool.com/the-extras/interesting-facts/',
  },
  {
    year: '1981',
    title: 'Ivor Spencer School 開校',
    body: 'Ivor Spencer 氏がロンドン郊外ダリッジに『Ivor Spencer School of Butlers and Personal Assistants』を開校。課程は6週間。',
    source: "Wikipedia『Ivor Spencer』",
    sourceUrl: 'https://en.wikipedia.org/wiki/Ivor_Spencer',
  },
  {
    year: '1990',
    title: 'Starkey International 設立',
    body: 'Mary Louise Starkey 氏が米国コロラド州デンバーに『Starkey International Institute for Household Management』を設立。',
    source: "Wikipedia『Starkey International Institute for Household Management』",
    sourceUrl: 'https://en.wikipedia.org/wiki/Starkey_International_Institute_for_Household_Management',
  },
  {
    year: '2009',
    title: 'The Charles MacPherson Academy 設立',
    body: 'カナダ・トロントに The Charles MacPherson Academy が設立された。',
    source: '同校公式サイト',
    sourceUrl: 'https://www.charlesmacpherson.com/',
  },
  {
    year: '2014',
    title: 'The International Butler Academy China 開校',
    body: '7月10日、中国・成都に The International Butler Academy China が開校した。',
    source: 'The International Butler Academy 公式サイト',
    sourceUrl: 'https://www.butlerschool.com/the-extras/interesting-facts/',
  },
  {
    year: '2018',
    title: 'Starkey International 閉校',
    body: 'Starkey International Institute for Household Management が閉校した。',
    source: "Wikipedia『Starkey International Institute for Household Management』",
    sourceUrl: 'https://en.wikipedia.org/wiki/Starkey_International_Institute_for_Household_Management',
  },
];

export default function ButlerSchoolHistoryPage() {
  const { lang } = useLanguageContext();
  const href = (path: string) => (lang === 'en' ? (path === '/' ? '/en' : `/en${path}`) : path);

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: lang === 'en' ? 'Home' : 'ホーム', item: 'https://butler-academy.jp/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: lang === 'en' ? 'Understanding Butlers' : '執事を知る',
        item: 'https://butler-academy.jp/butler-school-history',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: lang === 'en' ? 'History of Butler Schools' : '執事学校の歴史',
        item: 'https://butler-academy.jp/butler-school-history',
      },
    ],
  };

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: lang === 'en' ? 'History of Butler Schools' : '執事学校の歴史',
    description:
      lang === 'en'
        ? 'Butlers were not originally a profession learned in schools. Skills were passed down inside great houses. This page traces how butler education took shape as formal schools from the 1980s onward, with sources.'
        : '執事は、はじめから学校で学ぶ職業ではありませんでした。邸宅の中で受け継がれてきた技能が、1980年代以降どのように学校という形へ移っていったのかを、出典とともに整理します。',
    url: 'https://butler-academy.jp/butler-school-history',
    isPartOf: {
      '@type': 'WebSite',
      name: lang === 'en' ? 'Japan Butler Academy' : '日本執事アカデミー',
      url: 'https://butler-academy.jp/',
    },
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/butler-school-history" jsonLd={[breadcrumbStructuredData, webPageStructuredData]} />

        {/* Hero */}
        <section className="hero-billboard relative w-full h-[60vh] min-h-[460px] md:h-[82vh] md:min-h-[640px] overflow-hidden">
          <img
            src="/images/generated/philosophy-hero-2026.jpg"
            alt={lang === 'en' ? 'History of Butler Schools' : '執事学校の歴史'}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 hero-overlay"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="relative w-full px-8">
              <div className="max-w-[800px] mx-auto text-center">
                <div className="flex items-center justify-center gap-2 mb-8 text-[14px] tracking-[0.05em]">
                  <Link to={href('/')} className="text-foreground-200/70 hover:text-accent-400 transition-colors">
                    {lang === 'en' ? 'Home' : 'ホーム'}
                  </Link>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <span className="text-foreground-200/70">
                    {lang === 'en' ? 'Understanding Butlers' : '執事を知る'}
                  </span>
                  <i className="ri-arrow-right-s-line text-foreground-400/60 text-[12px]"></i>
                  <span className="text-accent-400">
                    {lang === 'en' ? 'History of Butler Schools' : '執事学校の歴史'}
                  </span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-50 tracking-[0.08em] mb-4">
                  執事学校の歴史
                </h1>
                <p className="section-title-en text-accent-400 text-[15px] md:text-[17px] tracking-[0.12em] mb-6">
                  HISTORY OF BUTLER SCHOOLS
                </p>
                <p className="text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.05em] leading-relaxed max-w-[620px] mx-auto">
                  執事は、はじめから学校で学ぶ職業ではありませんでした。邸宅の中で受け継がれてきた技能が、どのように学校という形へ移っていったのかを、出典とともにたどります。
                </p>
              </div>
            </div>
          </div>
        </section>

        <Breadcrumb
          items={[
            { label: '執事を知る', labelEn: 'Understanding Butlers' },
            { label: '執事学校の歴史', labelEn: 'History of Butler Schools' },
          ]}
        />

        {/* Lead */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-50">
            <div className="max-w-[860px] mx-auto px-8">
              <div className="text-center mb-10">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">INTRODUCTION</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  学校以前の執事
                </h2>
                <div className="gold-line mx-auto"></div>
              </div>
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">
                <p>
                  執事は、はじめから学校で学ぶ職業ではありませんでした。技能は学校ではなく邸宅の中で、先任者から後任者へ口伝と模倣で受け継がれていました。
                </p>
                <p>
                  執事の役職は19世紀ヴィクトリア朝に大きく育ち、20世紀に入って社会構造の変化とともに減少しました。1901年時点で英国の人口は3,800万人、家事使用人は女性1,700,000人・男性140,000人だったとされています。
                </p>
                <p className="text-[13px] text-foreground-400 tracking-[0.02em]">
                  出典:{' '}
                  <a
                    href="https://www.butlerschool.com/the-extras/interesting-facts/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors"
                  >
                    The International Butler Academy 公式サイト
                  </a>
                </p>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* Timeline */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-100/50">
            <div className="max-w-[860px] mx-auto px-8">
              <div className="text-center mb-14">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">TIMELINE</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  年表で見る執事教育
                </h2>
                <div className="gold-line mx-auto"></div>
              </div>

              <ol className="relative border-l border-accent-500/30 ml-3 md:ml-6 space-y-10">
                {timelineItems.map((item) => (
                  <li key={item.year} className="relative pl-8 md:pl-12">
                    <span className="absolute left-0 top-1.5 -translate-x-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50"></span>
                    <p className="text-accent-600 text-[13px] tracking-[0.16em] font-label mb-2">{item.year}</p>
                    <h3 className="font-heading text-[17px] md:text-[18px] text-foreground-900 tracking-[0.05em] mb-3">
                      {item.title}
                    </h3>
                    <p className="text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose mb-3">
                      {item.body}
                    </p>
                    <p className="text-[12px] md:text-[13px] text-foreground-400 tracking-[0.02em]">
                      出典:{' '}
                      <a
                        href={item.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors"
                      >
                        {item.source}
                      </a>
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        </FadeInSection>

        {/* (A) 邸宅の中で受け継がれた時代 */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-50">
            <div className="max-w-[860px] mx-auto px-8">
              <div className="text-center mb-10">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">ERA</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  邸宅の中で受け継がれた時代
                </h2>
                <div className="gold-line mx-auto"></div>

              <figure className="mt-10 mb-2 overflow-hidden md:order-2">
                <img
                  src="/images/generated/advanced-candlelight-2026.jpg"
                  alt="燭台に火を灯す執事の手元"
                  loading="lazy"
                  className="w-full h-[280px] md:h-[360px] object-cover object-top"
                />
              </figure>
              </div>
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">
                <p>
                  執事の役職は19世紀ヴィクトリア朝に大きく育ちました。大邸宅を支える家事使用人の階層のなかで、執事は家全体の運営を担う中核的な存在として位置づけられていました。
                </p>
                <p>
                  1901年時点で、英国の人口は3,800万人、家事使用人は女性1,700,000人・男性140,000人だったと記録されています。その後、20世紀に入って社会構造が変化するなかで、執事を含む家事使用人の数は減少していきました。
                </p>
                <p>
                  この時代、技能は学校ではなく邸宅の中で、先任者から後任者へ口伝と模倣によって受け継がれていました。体系化されたカリキュラムや資格制度ではなく、現場での実践そのものが教育の場でした。
                </p>
                <p className="text-[13px] text-foreground-400 tracking-[0.02em]">
                  出典:{' '}
                  <a
                    href="https://www.butlerschool.com/the-extras/interesting-facts/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors"
                  >
                    The International Butler Academy 公式サイト
                  </a>
                </p>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* (B) 学校という形が生まれた1980年代 */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-100/50">
            <div className="max-w-[860px] mx-auto px-8">
              <div className="text-center mb-10">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">SCHOOLS</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  学校という形が生まれた1980年代
                </h2>
                <div className="gold-line mx-auto"></div>

              <figure className="mt-10 mb-2 overflow-hidden md:order-1">
                <img
                  src="/images/generated/about-classroom-2026.jpg"
                  alt="教室で講師の手つきを見て学ぶ受講生"
                  loading="lazy"
                  className="w-full h-[280px] md:h-[360px] object-cover object-top"
                />
              </figure>
              </div>
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">
                <p>
                  1981年、Ivor Spencer 氏がロンドン郊外ダリッジに『Ivor Spencer School of Butlers and Personal Assistants』を開校しました。課程は6週間で、2006年時点の学費は約£5,375でした。Ivor Spencer 氏（1924年11月20日–2009年1月10日）は、2002年6月27日にバッキンガム宮殿でMBEを受章しています。
                </p>
                <p className="text-[13px] text-foreground-400 tracking-[0.02em]">
                  出典:{' '}
                  <a
                    href="https://en.wikipedia.org/wiki/Ivor_Spencer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors"
                  >
                    Wikipedia『Ivor Spencer』
                  </a>
                </p>
                <p>
                  1990年には、Mary Louise Starkey 氏が米国コロラド州デンバーに『Starkey International Institute for Household Management』を設立しました。課程はハウスホールドマネジメント8週間・エステートマネジメント4週間・サービスマネジメントシステム1週間で、学費は13,000ドル超。年間60名超が学びました。同校は2018年に閉校しています。
                </p>
                <p className="text-[13px] text-foreground-400 tracking-[0.02em]">
                  出典:{' '}
                  <a
                    href="https://en.wikipedia.org/wiki/Starkey_International_Institute_for_Household_Management"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors"
                  >
                    Wikipedia『Starkey International Institute for Household Management』
                  </a>
                </p>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* (C) 世界各地への広がり */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-50">
            <div className="max-w-[860px] mx-auto px-8">
              <div className="text-center mb-10">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">EXPANSION</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  世界各地への広がり
                </h2>
                <div className="gold-line mx-auto"></div>

              <figure className="mt-10 mb-2 overflow-hidden md:order-2">
                <img
                  src="/images/generated/blog-schools-worldwide-01.jpg"
                  alt="世界各地の執事教育機関を示すイメージ"
                  loading="lazy"
                  className="w-full h-[280px] md:h-[360px] object-cover object-top"
                />
              </figure>
              </div>
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">
                <p>
                  2009年、カナダ・トロントに The Charles MacPherson Academy が設立されました。
                </p>
                <p className="text-[13px] text-foreground-400 tracking-[0.02em]">
                  出典: 同校公式サイト
                </p>
                <p>
                  2014年7月10日には、中国・成都に The International Butler Academy China が開校しました。
                </p>
                <p className="text-[13px] text-foreground-400 tracking-[0.02em]">
                  出典:{' '}
                  <a
                    href="https://www.butlerschool.com/the-extras/interesting-facts/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors"
                  >
                    The International Butler Academy 公式サイト
                  </a>
                </p>
                <p>
                  現在はヨーロッパ・北米・アジア・中東・オセアニアに教育機関が存在します。各校の所在地や課程など、公式サイトで公表されている事実の整理は、海外の執事学校のページをご覧ください。
                </p>
                <div className="pt-4">
                  <Link
                    to={href('/overseas-schools')}
                    className="inline-flex items-center gap-2 text-accent-600 hover:text-accent-500 transition-colors"
                  >
                    <span className="text-[15px] tracking-[0.05em]">海外の執事学校を見る</span>
                    <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* (D) 職能団体と現在の規模 */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-100/50">
            <div className="max-w-[860px] mx-auto px-8">
              <div className="text-center mb-10">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">GUILD</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  職能団体と現在の規模
                </h2>
                <div className="gold-line mx-auto"></div>

              <figure className="mt-10 mb-2 overflow-hidden md:order-1">
                <img
                  src="/images/generated/association-cert-2026.jpg"
                  alt="認定証と白手袋"
                  loading="lazy"
                  className="w-full h-[280px] md:h-[360px] object-cover object-top"
                />
              </figure>
              </div>
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">
                <p>
                  International Guild of Professional Butlers は、世界に数百万人規模の専業執事がいると推計しています。同ギルドの活動会員は約10,000人で、その25%超が米国からであるとされています（年次の記載なし）。
                </p>
                <p className="text-[13px] text-foreground-400 tracking-[0.02em]">
                  出典:{' '}
                  <a
                    href="https://www.butlerschool.com/the-extras/interesting-facts/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors"
                  >
                    The International Butler Academy 公式サイト
                  </a>
                </p>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* (E) 日本における位置づけ */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-primary-900 text-foreground-200">
            <div className="max-w-[820px] mx-auto px-8 text-center">
              <p className="text-accent-400 text-[13px] tracking-[0.2em] mb-5 font-label">JAPAN</p>
              <h2 className="font-heading text-xl md:text-2xl text-foreground-50 tracking-[0.06em] mb-2">
                日本における位置づけ
              </h2>
              <p className="section-title-en text-accent-400 text-[15px] tracking-[0.12em] mb-6">
                JAPAN BUTLER ACADEMY
              </p>
              <div className="gold-line mx-auto mb-8"></div>

              <figure className="mt-10 mb-2 overflow-hidden md:order-2">
                <img
                  src="/images/generated/facility-odaiba-img.jpg"
                  alt="東京・お台場の本校"
                  loading="lazy"
                  className="w-full h-[280px] md:h-[360px] object-cover object-top"
                />
              </figure>
              <p className="text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.03em] leading-loose mb-8">
                日本執事アカデミーは、日本で唯一の学び舎を持つ執事養成学校です。東京・お台場の本校に実際に通えます。
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  to={href('/japan-butler-academy')}
                  className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-primary-900 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  日本執事アカデミーを見る
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
                <Link
                  to={href('/overseas-schools')}
                  className="inline-flex items-center gap-2 border border-accent-400/50 hover:border-accent-400 text-accent-400 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  海外の執事学校を見る
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </div>
          </section>
        </FadeInSection>

        {/* Sources */}
        <FadeInSection>

          <section className="relative py-20 md:py-28 bg-background-50">
            <div className="max-w-[1100px] mx-auto px-8">
              <div className="text-center mb-10">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">SOURCE PHOTOS</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">出典元サイトの写真</h2>
                <p className="text-[14px] text-foreground-500 leading-relaxed">本ページで出典として挙げている各教育機関の公式サイトに掲載されている写真です。各画像の下に出典元を記載しています。画像の著作権は各校に帰属します。</p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-8">
                <figure>
                  <img src="/images/overseas/tiba-netherlands-1.jpg" alt="The International Butler Academy の公式サイト掲載画像" loading="lazy" className="w-full h-[220px] md:h-[260px] object-cover object-center bg-background-100" />
                  <figcaption className="mt-2 text-[12px] leading-relaxed text-foreground-500">
                    出典: <a href="https://www.butlerschool.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent-600">The International Butler Academy 公式サイト</a>
                    <span className="block break-all text-foreground-400">https://www.butlerschool.com/_resources/themes/template/dist/img/Virtualtour.jpg</span>
                  </figcaption>
                </figure>
                <figure>
                  <img src="/images/overseas/tiba-netherlands-2.png" alt="The International Butler Academy の公式サイト掲載画像" loading="lazy" className="w-full h-[220px] md:h-[260px] object-cover object-center bg-background-100" />
                  <figcaption className="mt-2 text-[12px] leading-relaxed text-foreground-500">
                    出典: <a href="https://www.butlerschool.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent-600">The International Butler Academy 公式サイト</a>
                    <span className="block break-all text-foreground-400">https://www.butlerschool.com/assets/Uploads/LOGO_Tiba_new.png</span>
                  </figcaption>
                </figure>
                <figure>
                  <img src="/images/overseas/tiba-china-1.jpg" alt="The International Butler Academy China の公式サイト掲載画像" loading="lazy" className="w-full h-[220px] md:h-[260px] object-cover object-center bg-background-100" />
                  <figcaption className="mt-2 text-[12px] leading-relaxed text-foreground-500">
                    出典: <a href="https://www.butlerschool.com/the-extras/tiba-china/" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent-600">The International Butler Academy China 公式サイト</a>
                    <span className="block break-all text-foreground-400">https://www.butlerschool.com/assets/GalleryImage/tiba_china_100112__FillWzQwMCwzMDBd.jpg</span>
                  </figcaption>
                </figure>
                <figure>
                  <img src="/images/overseas/tiba-china-2.jpg" alt="The International Butler Academy China の公式サイト掲載画像" loading="lazy" className="w-full h-[220px] md:h-[260px] object-cover object-center bg-background-100" />
                  <figcaption className="mt-2 text-[12px] leading-relaxed text-foreground-500">
                    出典: <a href="https://www.butlerschool.com/the-extras/tiba-china/" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent-600">The International Butler Academy China 公式サイト</a>
                    <span className="block break-all text-foreground-400">https://www.butlerschool.com/assets/GalleryImage/tiba_china_100113__FillWzQwMCwzMDBd.jpg</span>
                  </figcaption>
                </figure>
                <figure>
                  <img src="/images/overseas/charles-macpherson-academy-1.jpg" alt="The Charles MacPherson Academy の公式サイト掲載画像" loading="lazy" className="w-full h-[220px] md:h-[260px] object-cover object-center bg-background-100" />
                  <figcaption className="mt-2 text-[12px] leading-relaxed text-foreground-500">
                    出典: <a href="https://www.charlesmacpherson.com/about-the-academy/" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent-600">The Charles MacPherson Academy 公式サイト</a>
                    <span className="block break-all text-foreground-400">https://www.charlesmacpherson.com/wp-content/uploads/2022/10/cropped-CMA_M-270x270.jpg</span>
                  </figcaption>
                </figure>
                <figure>
                  <img src="/images/overseas/charles-macpherson-academy-2.png" alt="The Charles MacPherson Academy の公式サイト掲載画像" loading="lazy" className="w-full h-[220px] md:h-[260px] object-cover object-center bg-background-100" />
                  <figcaption className="mt-2 text-[12px] leading-relaxed text-foreground-500">
                    出典: <a href="https://www.charlesmacpherson.com/about-the-academy/" target="_blank" rel="noopener noreferrer" className="underline hover:text-accent-600">The Charles MacPherson Academy 公式サイト</a>
                    <span className="block break-all text-foreground-400">https://www.charlesmacpherson.com/wp-content/uploads/2022/10/CM_logo_horz_2021.png</span>
                  </figcaption>
                </figure>
              </div>
            </div>
          </section>
          <section className="relative py-16 md:py-20 bg-background-50">
            <div className="max-w-[860px] mx-auto px-8">
              <div className="text-center mb-10">
                <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">SOURCE</p>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  出典一覧
                </h2>
                <div className="gold-line mx-auto"></div>
              </div>
              <ul className="space-y-4 text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose">
                <li className="flex gap-3">
                  <span className="text-accent-500 shrink-0">・</span>
                  <a
                    href="https://www.butlerschool.com/the-extras/interesting-facts/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors break-all"
                  >
                    The International Butler Academy 公式サイト（https://www.butlerschool.com/the-extras/interesting-facts/）
                  </a>
                </li>
                <li className="flex gap-3">
                  <span className="text-accent-500 shrink-0">・</span>
                  <a
                    href="https://en.wikipedia.org/wiki/Ivor_Spencer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors break-all"
                  >
                    Wikipedia『Ivor Spencer』（https://en.wikipedia.org/wiki/Ivor_Spencer）
                  </a>
                </li>
                <li className="flex gap-3">
                  <span className="text-accent-500 shrink-0">・</span>
                  <a
                    href="https://en.wikipedia.org/wiki/Starkey_International_Institute_for_Household_Management"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors break-all"
                  >
                    Wikipedia『Starkey International Institute for Household Management』（https://en.wikipedia.org/wiki/Starkey_International_Institute_for_Household_Management）
                  </a>
                </li>
              </ul>
            </div>
          </section>
        </FadeInSection>

        {/* Related links */}
        <FadeInSection>
          <section className="relative py-20 md:py-24 bg-background-100/50">
            <div className="max-w-[760px] mx-auto px-8 text-center">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">
                関連リンク
              </h2>
              <div className="gold-line mx-auto mb-8"></div>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  to={href('/butler-school')}
                  className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  執事学校とは？
                </Link>
                <Link
                  to={href('/butler-training-school')}
                  className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  執事養成学校とは？
                </Link>
                <Link
                  to={href('/butler-job')}
                  className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  執事の仕事とは？
                </Link>
                <Link
                  to={href('/overseas-schools')}
                  className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  海外の執事学校
                </Link>
              </div>
            </div>
          </section>
        </FadeInSection>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={lang === 'en' ? 'Back to top' : 'ページ上部へ'}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
